import { useCallback, memo, useEffect } from 'react'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import { styled } from '@mui/material/styles'
import Button from '@mui/material/Button'
import { observer } from 'mobx-react'
import { NavigateFunction } from 'react-router-dom'
import CategoryLayout from 'components/Layout/Category'
import { scrollToTop } from '../../utils/pollard/scroll'
import useStore from 'stores/utils/hooks/useStore'
import { AmplitudeEvent } from 'utils/tracking/amplitude/amplitude.interface'
import { trackInAmplitude } from 'utils/tracking/amplitude'
import { getConfig, ConfigName } from 'config/utils/config'
import { toJS } from 'mobx'
import { flattenFieldErrors } from './utils'

interface Props {
  pageName: string
  navigate: NavigateFunction
  configToNavigate?: ConfigName
}

const StyledHint = styled('p')(({ theme }) => ({
  padding: '1rem',
  border: `0.0625rem solid ${theme.palette.error.main}`,
  borderRadius: '0.125rem',
  fontSize: '1rem',
  color: theme.palette.error.main,
  textAlign: 'center'
}))

const PreviousPageErrors = observer(
  ({ pageName, navigate, configToNavigate }: Props) => {
    const rootStore = useStore()
    const { history, page, fieldErrors } = useStore()

    // TODO: Use page title from metadata :)
    const pageTitle = `Seite ${page.getPageIndex(pageName) + 1}`

    const jumpToPageWithError = useCallback(async () => {
      trackInAmplitude(AmplitudeEvent.PreviousPageErrorNavigationClicked)
      if (configToNavigate) {
        const config = await getConfig(configToNavigate)
        await rootStore.page.setConfiguration(config)
      }

      history.push(pageName)
      navigate(pageName)
      scrollToTop(rootStore.isInIframe)
    }, [
      history,
      pageName,
      navigate,
      rootStore.isInIframe,
      configToNavigate,
      rootStore.page
    ])

    useEffect(() => {
      const flatErrors = flattenFieldErrors(toJS(fieldErrors.errors))
      trackInAmplitude(AmplitudeEvent.PreviousPageErrorNavigationShown, {
        pageName: pageName,
        errors: flatErrors
      })
    }, [])

    return (
      <CategoryLayout>
        <StyledHint>
          {`Um Angebote zu erhalten, beheben Sie bitte zunächst die Fehler auf "${pageTitle}".`}
        </StyledHint>
        <Button
          name="previous-page-with-errors"
          size="large"
          fullWidth
          variant="contained"
          color="secondary"
          startIcon={<ArrowBackIcon />}
          onClick={jumpToPageWithError}
        >
          {`Zurück zu ${pageTitle}`}
        </Button>
      </CategoryLayout>
    )
  }
)

export default memo(PreviousPageErrors)
